import { DateTime } from 'luxon';

export class DateUtil {
  private static cache: { [key: string]: DateTime } = {};

  /**
   * Safely converts Date strings or date objects to JS Date objects
   */
  static toDate(date: Date | string | undefined): Date | null {
    if (date instanceof Date) {
      return date;
    } else if (typeof date === 'string') {
      return new Date(date);
    }
    return null;
  }

  /**
   * Safely converts Date objects, date strings, or DateTime objects to DateTime
   * Handles backend date strings (ISO format) and Date objects
   */
  static toDateTime(date: Date | string | DateTime | undefined): DateTime {
    if (date instanceof DateTime) {
      return date;
    }
    if (date instanceof Date) {
      return DateTime.fromJSDate(date);
    }
    // Handle string dates - try ISO format first, fallback to JS Date parsing
    const isoResult = DateTime.fromISO(date);
    if (isoResult.isValid) {
      return isoResult;
    }
    return DateTime.fromJSDate(new Date(date));
  }

  /**
   * Formats dates as a date-only string (yyyy-MM-dd) for backend requests
   */
  static toDateOnlyString(date: Date | string | DateTime | undefined): string {
    return this.toDateTime(date).toFormat('yyyy-MM-dd');
  }

  static addHverdage(days: number, date: DateTime): DateTime {
    let dateClone = date;

    // If starting on a non-business day, first move to next/previous business day
    if (!this.isHverdag(dateClone)) {
      if (days >= 0) {
        // Moving forward: find next business day
        while (!this.isHverdag(dateClone)) {
          dateClone = dateClone.plus({ days: 1 });
        }
      } else {
        // Moving backward: find previous business day
        while (!this.isHverdag(dateClone)) {
          dateClone = dateClone.minus({ days: 1 });
        }
      }
    }

    // Now add/subtract the requested business days
    let count = 0;
    while (count < Math.abs(days)) {
      dateClone = dateClone.plus({ days: days < 0 ? -1 : 1 });
      if (this.isHverdag(dateClone)) {
        count++;
      }
    }
    return dateClone;
  }

  static isHverdag(date: DateTime | Date | string): boolean {
    const dt = this.toDateTime(date);
    if (dt.weekday === 6 || dt.weekday === 7) {
      // weekend (Saturday=6, Sunday=7)
      return false;
    }
    return !this.isHelligdag(dt);
  }

  static isHelligdag(date: DateTime): boolean {
    if (date.ordinal === 1) {
      // Nytårsdag
      return true;
    } else if (date.month === 6 && date.day === 5) {
      // Grundlovsdag
      return true;
    } else if (date.month === 12 && (date.day === 24 || date.day === 25 || date.day === 26)) {
      // Juleaften, 1,2.Juledag
      return true;
    } else if (
      this.isSkaerTorsdag(date) ||
      this.isLangFredag(date) ||
      this.isPaaskeSoendag(date) ||
      this.isAndenPaaskeDag(date) ||
      this.isKristiHimmelfartsdag(date) ||
      this.isPinsedag(date) ||
      this.isAndenPinsedag(date)
    ) {
      return true;
    }
    return false;
  }

  private static getPaaskeSoendag(date: DateTime): DateTime {
    const year = date.year;
    if (this.cache['paaske' + year]) {
      return this.cache['paaske' + year];
    }
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const L = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * L) / 451);
    const month = Math.floor((h + L - 7 * m + 114) / 31);
    const day = ((h + L - 7 * m + 114) % 31) + 1;
    const paaskeSoendag = DateTime.fromObject({ year, month, day }).setLocale('da');
    this.cache['paaske' + year] = paaskeSoendag;
    return paaskeSoendag;
  }

  private static isSkaerTorsdag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).minus({ days: 3 }).hasSame(date, 'day');
  }

  private static isLangFredag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).minus({ days: 2 }).hasSame(date, 'day');
  }

  private static isPaaskeSoendag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).hasSame(date, 'day');
  }

  private static isAndenPaaskeDag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).plus({ days: 1 }).hasSame(date, 'day');
  }

  private static isKristiHimmelfartsdag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).plus({ days: 39 }).hasSame(date, 'day');
  }

  private static isPinsedag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).plus({ days: 49 }).hasSame(date, 'day');
  }

  private static isAndenPinsedag(date: DateTime): boolean {
    return this.getPaaskeSoendag(date).plus({ days: 50 }).hasSame(date, 'day');
  }
}
